import { forwardRef, useImperativeHandle, useState } from "react";
import { copyField, copyAllFields } from "../../../utilities/clipboard";
import { CopyIcon } from "asset/icons";
import { useSelector } from "react-redux";

const filterValid = (fields) =>
    fields.filter((f) => {
        if (f.value === null || f.value === undefined) return false;
        if (typeof f.value === "string") return f.value.trim() !== "";
        if (Array.isArray(f.value)) return f.value.length > 0;
        return true;
    });

const buildFields = (data, { isKES, isUSD }) => {
    const rails = Array.isArray(data?.paymentRails)
        ? data.paymentRails
        : Array.isArray(data?.meta?.deposit_instructions?.payment_rails)
        ? data.meta.deposit_instructions.payment_rails
        : [];

    const primary = [
        { label: "Bank name", value: data?.bankName || data?.bank },
        { label: "Account number", value: data?.accountNumber },
        { label: "Account name", value: data?.accountName },
        ...(rails.length ? [{ label: "Payment rails", value: rails }] : []),
        ...(isKES && data?.branchName ? [{ label: "Branch name", value: data.branchName }] : []),
        ...(isKES && data?.bankType ? [{ label: "Bank type", value: data.bankType }] : []),
        ...(isUSD
            ? [
                  { label: "Bank address", value: data?.bankAddress },
                  { label: "Bank code", value: data?.bankCode },
                  { label: "Routing number", value: data?.routingNumber },
              ]
            : []),
    ];

    const secondary = [
        { label: "IBAN", value: data?.iban },
        { label: "Swift code", value: data?.swift },
    ];

    return { primary: filterValid(primary), secondary: filterValid(secondary) };
};

const shortBankLabel = (name, fallback) => {
    if (!name) return fallback;
    const stripped = String(name).replace(/\s*bank$/i, "").trim();
    return stripped || name;
};

const labelTypography = {
    color: "var(--aku-colour-text-body, #494E6E)",
    fontFamily: '"Public Sans", sans-serif',
    fontSize: "var(--aku-font-size-xs, 12px)",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "140%",
};

const valueTypography = {
    color: "var(--aku-colour-text-heading, #161827)",
    fontFamily: '"Public Sans", sans-serif',
    fontSize: "var(--aku-font-size-xs, 12px)",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "normal",
};

const FieldRow = ({ label, value, onCopy }) => {
    const isArray = Array.isArray(value);
    const displayText = isArray ? value.map(String).join(", ") : value;

    return (
        <div className="flex justify-between items-start gap-4">
            <p style={labelTypography}>{label}</p>

            <div className="flex items-start gap-2 justify-end min-w-0">
                {isArray ? (
                    <div className="flex flex-wrap gap-2 justify-end">
                        {value.length ? (
                            value.map((r) => (
                                <span
                                    key={r}
                                    className="px-2 py-1 rounded-full text-xs font-semibold bg-[#E9EDFF] text-[#111827] border border-[#D7DCFF]"
                                >
                                    {String(r).toUpperCase()}
                                </span>
                            ))
                        ) : (
                            <p style={valueTypography}>—</p>
                        )}
                    </div>
                ) : (
                    <p style={valueTypography} className="break-words text-right">
                        {displayText ?? "—"}
                    </p>
                )}

                {!!displayText && (
                    <button
                        onClick={() => onCopy(displayText, label)}
                        className="shrink-0 p-1"
                        aria-label={`Copy ${label}`}
                    >
                        <CopyIcon className="w-4 h-4 [&_path]:stroke-[#9CA3AF]" />
                    </button>
                )}
            </div>
        </div>
    );
};

const BankTransferTab = forwardRef(({ walletData, providers }, ref) => {
    const { currency: selectedCurrency } = useSelector((s) => s);
    const currency = walletData?.currency || selectedCurrency;
    const isKES = String(currency || "").toUpperCase() === "KES";
    const isUSD = String(currency || "").toUpperCase() === "USD";

    const accounts =
        Array.isArray(providers) && providers.length > 1
            ? providers
            : walletData
            ? [walletData]
            : [];

    const [activeIndex, setActiveIndex] = useState(0);
    const activeAccount = accounts[activeIndex] || accounts[0];
    const { primary, secondary } = activeAccount
        ? buildFields(activeAccount, { isKES, isUSD })
        : { primary: [], secondary: [] };

    useImperativeHandle(ref, () => ({
        copyActive: () => {
            copyAllFields([...primary, ...secondary]);
        },
    }));

    if (!accounts.length) return null;

    const handleCopy = (value, label) => copyField(value, label);

    return (
        <div className="mt-2 text-xs">
            <p className="text-[#6F739B] text-xs mb-4">Transfer to any of the accounts below</p>

            {accounts.length > 1 && (
                <div className="flex gap-2 mb-4">
                    {accounts.map((acc, idx) => {
                        const label = shortBankLabel(
                            acc?.bankName || acc?.bank,
                            `Account ${idx + 1}`
                        );
                        const isActive = idx === activeIndex;

                        return (
                            <button
                                key={acc?._id || acc?.accountNumber || idx}
                                onClick={() => setActiveIndex(idx)}
                                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${
                                    isActive
                                        ? "bg-[#EDE6FF] text-[#6F00FF]"
                                        : "text-[#6B7280] hover:text-[#111827]"
                                }`}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
            )}

            <div className="bg-[#F8F9FC] rounded-xl p-4 max-h-[320px] overflow-y-auto">
                <div className="space-y-4">
                    {primary.map((f) => (
                        <FieldRow key={f.label} {...f} onCopy={handleCopy} />
                    ))}
                </div>

                {!!secondary.length && (
                    <>
                        <div className="border-t border-dashed border-gray-300 my-4" />
                        <div className="space-y-4">
                            {secondary.map((f) => (
                                <FieldRow key={f.label} {...f} onCopy={handleCopy} />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
});

export default BankTransferTab;
